// BankAccountInDubaiArticle.tsx
import { Col, Row } from "react-bootstrap";
import MaterialIcon from "../../material-icon";
import { colorPalette } from "../../../utils/theme";
import useIsMobile from "../../../hooks/useIsMobile";
import { ArticleImage, ArticleTitle, ArticleSubtitle, SharingContainer, ArticleDescription, Headline, ThreeDots, BulletPointList, BulletPointItem, ArticleText, FilterContainer, StyledLink, AuthorRole, AuthorAvatar, AuthorName, SubHeadline, Divider, ArticleImageContainer, PlayButton, YouTubeFrame, VideoLoadingContainer, NumberedItem, NumberedList } from "../article.styles";
import BankAccountInDubaiImage from "../../../assets/images/articles/bank-account-in-dubai-large.webp";
import BankAccountInDubaiMobileImage from "../../../assets/images/articles/bank-account-in-dubai-mobile.webp";
import Filter from "../../filter/filter";
import CTAButton from "../../cta-button/cta-button";
import { useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { openExternalLink } from "../../../utils/navigation";

interface BankAccountInDubaiArticleProps {
    onClickShare: () => void;
}

const BankAccountInDubaiArticle: React.FC<BankAccountInDubaiArticleProps> = ({ onClickShare }) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    const renderHeaderContent = useCallback(() => {
        return (
            <ArticleImageContainer>
                <ArticleImage
                    src={isMobile ? BankAccountInDubaiMobileImage : BankAccountInDubaiImage}
                    alt="Article Thumbnail"
                    showBorderRadius={!isMobile}
                />
            </ArticleImageContainer>
        );
    }, []);

    return (
        <>
            {isMobile && (
                renderHeaderContent()
            )}

            <Row className="justify-content-center flex-grow-1" style={{ paddingLeft: 16, paddingRight: 16 }}>
                <Col xs={12} lg={8} xl={6}>
                    <ArticleTitle>{t("articles.bank_account.title")}</ArticleTitle>
                    <ArticleSubtitle>{t("articles.bank_account.subtitle")}</ArticleSubtitle>

                    <SharingContainer>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            <AuthorAvatar />
                            <div>
                                <AuthorName>DGMA Legal Services</AuthorName>
                                <AuthorRole>Partner Content</AuthorRole>
                            </div>
                        </div>
                        <MaterialIcon icon="share" size={24} color={colorPalette.textDark} onClick={onClickShare} />
                    </SharingContainer>

                    {!isMobile && (
                        renderHeaderContent()
                    )}

                    <ArticleDescription>{t("articles.bank_account.intro")}</ArticleDescription>

                    <ThreeDots />

                    <Headline>{t("articles.bank_account.advantages_title")}</Headline>
                    <ArticleText>{t("articles.bank_account.advantages_intro")}</ArticleText>
                    <BulletPointList>
                        <BulletPointItem>
                            <strong>{t("articles.bank_account.adv_flexibility_title")}</strong><br />
                            {t("articles.bank_account.adv_flexibility_text")}
                        </BulletPointItem>
                        <BulletPointItem>
                            <strong>{t("articles.bank_account.adv_currency_title")}</strong><br />
                            {t("articles.bank_account.adv_currency_text")}
                        </BulletPointItem>
                        <BulletPointItem>
                            <strong>{t("articles.bank_account.adv_protection_title")}</strong><br />
                            {t("articles.bank_account.adv_protection_text")}
                        </BulletPointItem>
                        <BulletPointItem>
                            <strong>{t("articles.bank_account.adv_conditions_title")}</strong><br />
                            {t("articles.bank_account.adv_conditions_text")}
                        </BulletPointItem>
                    </BulletPointList>

                    <ThreeDots />

                    <Headline>{t("articles.bank_account.who_title")}</Headline>
                    <ArticleText>{t("articles.bank_account.who_intro")}</ArticleText>
                    <BulletPointList>
                        <BulletPointItem>
                            <strong>{t("articles.bank_account.who_investors_title")}</strong><br />
                            {t("articles.bank_account.who_investors_text")}
                        </BulletPointItem>
                        <BulletPointItem>
                            <strong>{t("articles.bank_account.who_private_title")}</strong><br />
                            {t("articles.bank_account.who_private_text")}
                        </BulletPointItem>
                        <BulletPointItem>
                            <strong>{t("articles.bank_account.who_diversifying_title")}</strong><br />
                            {t("articles.bank_account.who_diversifying_text")}
                        </BulletPointItem>
                    </BulletPointList>

                    <ThreeDots />

                    <Headline>{t("articles.bank_account.steps_title")}</Headline>
                    <ArticleText>{t("articles.bank_account.steps_intro")}</ArticleText>
                    <SubHeadline>{t("articles.bank_account.steps_subtitle")}</SubHeadline>
                    <NumberedList>
                        <NumberedItem>
                            <strong>{t("articles.bank_account.step1_title")}</strong><br />
                            {t("articles.bank_account.step1_text")}
                        </NumberedItem>
                        <NumberedItem>
                            <strong>{t("articles.bank_account.step2_title")}</strong><br />
                            {t("articles.bank_account.step2_text")}
                        </NumberedItem>
                        <NumberedItem>
                            <strong>{t("articles.bank_account.step3_title")}</strong><br />
                            {t("articles.bank_account.step3_text")}
                        </NumberedItem>
                        <NumberedItem>
                            <strong>{t("articles.bank_account.step4_title")}</strong><br />
                            {t("articles.bank_account.step4_text")}
                        </NumberedItem>
                        <NumberedItem>
                            <strong>{t("articles.bank_account.step5_title")}</strong><br />
                            {t("articles.bank_account.step5_text")}
                        </NumberedItem>
                        <NumberedItem>
                            <strong>{t("articles.bank_account.step6_title")}</strong><br />
                            {t("articles.bank_account.step6_text")}
                        </NumberedItem>
                        <NumberedItem>
                            <strong>{t("articles.bank_account.step7_title")}</strong><br />
                            {t("articles.bank_account.step7_text")}
                        </NumberedItem>
                    </NumberedList>

                    <ThreeDots />

                    <SubHeadline>{t("articles.bank_account.conclusion_subtitle")}</SubHeadline>
                    <ArticleText>{t("articles.bank_account.conclusion_text")}</ArticleText>

                    <Divider />

                    <Headline>{t("articles.bank_account.cta_title")}</Headline>
                    <CTAButton
                        variant="primary"
                        size="M"
                        label={t("pages.knowledge_hub.articles.bank_account.book_free_consultation")}
                        icon="open_in_new"
                        onClick={() => openExternalLink('https://calendly.com/nalin-dgma-legal')}
                        fullWidth={isMobile}
                    />

                    <Divider />

                    <SubHeadline>{t("pages.knowledge_hub.articles.bank_account.about_dgma")}</SubHeadline>
                    <ArticleText>{t("articles.bank_account.about_dgma_text1")}</ArticleText>
                    <ArticleText>{t("articles.bank_account.about_dgma_text2")}</ArticleText>

                    <CTAButton
                        variant="ghost"
                        size="L"
                        label={t("pages.knowledge_hub.articles.bank_account.visit_dgma")}
                        icon="open_in_new"
                        onClick={() => openExternalLink('https://dgma-legal.com/')}
                        noPadding
                    />

                    {/*<FilterContainer>
                        <Filter label="Handover" disabled />
                        <Filter label="Mietverwaltung" disabled />
                        <Filter label="Services" disabled />
                    </FilterContainer>*/}
                </Col>
            </Row>
        </>
    );
};

export default BankAccountInDubaiArticle;
